import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useAuth } from "@/contexts/AuthContext";

export const BackButtonHandler = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listenerHandle: { remove: () => Promise<void> } | null = null;
    // `addListener` é assíncrono: se o effect for limpo antes do await resolver,
    // `listenerHandle` ainda é null e o cleanup não removeria nada — deixando um
    // listener órfão (de um `user` defasado). O flag garante a remoção mesmo assim.
    let cancelled = false;

    const setupBackButtonListener = async () => {
      const handle = await CapacitorApp.addListener("backButton", () => {
        // 1. Check for open modals, dialogs, or sheets (Radix UI / Tailwind overlay elements)
        const openModal = document.querySelector(
          '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
        );

        if (openModal) {
          // Dispatch Escape key event to close active overlay
          const escapeEvent = new KeyboardEvent("keydown", {
            key: "Escape",
            code: "Escape",
            keyCode: 27,
            which: 27,
            bubbles: true,
            cancelable: true,
          });
          document.dispatchEvent(escapeEvent);
          return;
        }

        // 2. Normalize current pathname
        const currentPath = window.location.pathname.replace(/\/$/, "") || "/";

        if (currentPath === "/auth") {
          // Navigate back on /auth or return to onboarding/home page
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate("/", { replace: true });
          }
          return;
        }

        // Visitors reach the dashboard from onboarding. Unlike an authenticated
        // user's dashboard, it must not behave as an app root: Android back must
        // return to onboarding, never exit the app. We navigate to "/" directly
        // (deterministic) instead of relying on navigate(-1)/history.length, which
        // is unreliable in the Capacitor WebView and could otherwise let a visitor
        // fall through to the exitApp branch below.
        if (currentPath === "/dashboard" && !user) {
          navigate("/", { replace: true });
          return;
        }

        const rootPaths = ["/dashboard", "/"];

        if (rootPaths.includes(currentPath)) {
          // Exit app when pressing back on root screens
          CapacitorApp.exitApp();
        } else {
          // Navigate back on sub-pages
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            // Fallback to dashboard if no prior history stack
            navigate("/dashboard", { replace: true });
          }
        }
      });
      if (cancelled) {
        // O effect já foi limpo enquanto o addListener resolvia → remove na hora.
        handle.remove();
        return;
      }
      listenerHandle = handle;
    };

    setupBackButtonListener();

    return () => {
      cancelled = true;
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, [navigate, user]);

  return null;
};

export default BackButtonHandler;
